import React, { useEffect } from 'react';
import { Button, ButtonGroup, Col, DropdownButton, Dropdown, Form, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import Block from '../Block';
import Icon from '../Icon';
import { useDeleteDmpMutation, usePushDmpMutation, useUpdateDmpMutation } from '../../api/dmpApiSlice';
import { FormGroup, Select, useForm, Yup } from '../components';
import useDeleteConfirmation from '../useDeleteConfirmation';

const Dmp = ({dmp, onSaved, disabled}) => {
  const { t } = useTranslation();
  const [ audienceOptions, setAudienceOptions ] = useState([]);
  const [ update ] = useUpdateDmpMutation();
  const [ deleteDmp ] = useDeleteDmpMutation();
  const [ pushDmp ] = usePushDmpMutation();

  const initialValues = {
    name: '',
    audienceIds: []
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(),
    audienceIds: Yup.array(Yup.number())
  })

  const selectIntoAttributes = (formData, dmp) => {
    const newAudienceIds = formData.audienceIds || [];
    const existingAudienceIds = dmp.audience_dmps.map(a => a.data_product_id);
    const audience_dmps_attributes = [];
    dmp.audience_dmps.forEach(audienceDmp => {
      if (newAudienceIds.includes(audienceDmp.data_product_id)) {
        audience_dmps_attributes.push({
          id: audienceDmp.id,
          data_product_id: audienceDmp.data_product_id
        });
      } else {
        audience_dmps_attributes.push({
          id: audienceDmp.id,
          data_product_id: audienceDmp.data_product_id,
          _destroy: 1
        });
      }
    });
    newAudienceIds.forEach(audienceId => {
      if (!existingAudienceIds.includes(audienceId)) {
        audience_dmps_attributes.push({
          data_product_id: audienceId
        });
      }
    });
    return audience_dmps_attributes;
  }

  const onSubmit = (e) => {
    e.preventDefault();
    const audience_dmps_attributes = selectIntoAttributes(formData, dmp);
    const data = {
      dmp: {
        dmpId: dmp.slug,
        ...formData,
        audience_dmps_attributes: audience_dmps_attributes
      }
    }
    update(data).unwrap()
      .then(() => {
        onSaved();
      })
      .catch((error) => {
        console.error('Failed to update DMP:', error);
      });
  }

  const { getInputProps, formProps, formData, setFormData, handleInputChange, submitButtonProps } = useForm({
    defaultObject: dmp,
    initialValues: initialValues,
    disabled: disabled,
    validationSchema: validationSchema,
    onSubmit: onSubmit
  });

  const {DeleteConfirmation, deleteConfirmationProps, confirm} = useDeleteConfirmation()

  const resetFromDmp = () => {
    setFormData({ ...dmp, audienceIds: dmp.audience_dmps.map(a => a.data_product_id) });
  }

  const onCancel = () => {
    resetFromDmp();
  }

  const onDeleteDmp = () => {
    deleteDmp(dmp.slug)
  }

  const onDelete = () => {
    confirm(dmp.name, () => onDeleteDmp())
  }

  const onPush = () => {
    pushDmp(dmp)
  }

  const onPushTaxonomy = () => {
    pushDmp({ ...dmp, taxonomy_only: true })
  }

  const onPushSegments = () => {
    pushDmp({ ...dmp, segments_only: true })
  }

  useEffect(() => {
    resetFromDmp();
  }, [dmp])

  useEffect(() => {
    setAudienceOptions(dmp.available_audiences.map(a => ({ value: a.id, label: a.name })));
  }, [dmp])

  return (
    <Form>
      <Block key={dmp.slug} title={<div className="mb-2">
          {dmp.name}
          <DropdownButton as={ButtonGroup} title={t("dmp.push")} size="sm" id="bg-nested-dropdown" style={{ float: 'right' }}>
            <Dropdown.Item eventKey="1" onClick={onPushTaxonomy} >
              <Icon icon='play' text={t("dmp.pushTaxonomy")} />
            </Dropdown.Item>
            <Dropdown.Item eventKey="2" onClick={onPushSegments} >
              <Icon icon='play' text={t("dmp.pushSegments")} />
            </Dropdown.Item>
            <Dropdown.Item eventKey="3" onClick={onPush} >
              <Icon icon='play' text={t("dmp.pushAll")} />
            </Dropdown.Item>
          </DropdownButton>
        </div>}>
        <FormGroup label={t("dmp.audiences")} {...getInputProps("audienceIds")}>
          <Select isMulti isClearable options={audienceOptions}/>
        </FormGroup>
        { !disabled && <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onDelete} className="mr-2">
            <Icon icon='trash' text={t("delete")} />
          </Button>
          <Button variant="secondary" onClick={onCancel} className="mr-2">
            <Icon icon='cancel' text={t("cancel")} />
          </Button>
          <Button variant="primary" type="submit" onClick={onSubmit} className="mr-2">
            <Icon icon='save' text={t("save")} />
          </Button>
        </div>}
        <DeleteConfirmation
          placeholder={t("dmp.deletePlaceholder")}
          {...deleteConfirmationProps}
          confirmValue={t("delete")}
        />
      </Block>
    </Form>
  )
}

export default Dmp;
